import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import {
  triggerSynkkaImport,
  triggerUserSynkkaImport,
} from "../api/synkka.api";

const useSynkkaIntegrations = ({ isAdmin }: { isAdmin: boolean }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef]),
  );

  const importSynkkaProducts = async (synkkaId?: string) => {
    const result =
      await adminImportSynkkaProductsMutation.mutateAsync(synkkaId);

    return result;
  };

  const userImportSynkkaProducts = async (companyId: number) => {
    const result = await userImportSynkkaProductsMutation.mutateAsync({
      companyId,
    });
    return result;
  };

  const adminImportSynkkaProductsMutation = useMutation({
    mutationFn: (synkkaId?: string) =>
      triggerSynkkaImport(bearerToken as string, synkkaId as string),
    onError: (error) => {
      console.error("Failed to trigger Synkka import:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-synkka-import-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-synkka-import-success"),
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["adminCompanies"] });
    },
  });

  const userImportSynkkaProductsMutation = useMutation({
    mutationFn: ({ companyId }: { companyId: number }) =>
      triggerUserSynkkaImport(bearerToken as string, companyId),
    onError: (error) => {
      console.error("Failed to trigger Synkka import:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-synkka-import-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-synkka-import-success"),
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });

  const isImportingSynkka =
    adminImportSynkkaProductsMutation.isPending ||
    userImportSynkkaProductsMutation.isPending;

  return {
    userImportSynkkaProducts,
    importSynkkaProducts,
    isImportingSynkka,
  };
};

export default useSynkkaIntegrations;
